import {
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  column,
  emptyCell,
  numericColumn,
  simpleHeader,
} from "@homebound/beam";
import { Percentage, emptyCellDash, priceCell } from "src/components";
import {
  PurchaseOrderPage_CommitmentFragment,
  PurchaseOrderPage_DrawFragment,
  PurchaseOrderPage_TaskFragment,
} from "src/generated/graphql-types";

type PaymentScheduleTableProps = { commitment: PurchaseOrderPage_CommitmentFragment };

export function PaymentScheduleTable({ commitment }: PaymentScheduleTableProps) {
  const { draws, committedInCents, depositInBasisPoints } = commitment;
  return (
    <div css={Css.mt6.$}>
      <div css={Css.lgSb.mb4.$}>Payment Schedule</div>
      <GridTable
        as="table"
        columns={createPaymentScheduleColumns(committedInCents)}
        rows={createPaymentScheduleRows(draws, depositInBasisPoints)}
        style={{ bordered: false, allWhite: true }}
        rowStyles={{
          header: { cellCss: Css.gray600.$ },
          total: { cellCss: Css.smSb.mt2.$ },
        }}
      />
    </div>
  );
}

type HeaderRow = { kind: "header" };
type DrawLineRow = {
  kind: "draw";
  data: Omit<PurchaseOrderPage_DrawFragment, "task"> & {
    isDeposit?: boolean;
    task: PurchaseOrderPage_TaskFragment | undefined;
  };
};
type TotalRow = { kind: "total"; data: undefined };
type Row = HeaderRow | DrawLineRow | TotalRow;

function createPaymentScheduleColumns(committedInCents: number): GridColumn<Row>[] {
  return [
    column<Row>({
      header: () => "Bill Terms",
      draw: ({ isDeposit }) => (isDeposit ? "Deposit" : "Draw"),
      total: emptyCell,
    }),
    column<Row>({
      header: () => "Associated Task",
      draw: ({ task }) => task?.name ?? emptyCellDash,
      total: emptyCell,
    }),
    column<Row>({
      header: () => "Description",
      draw: (row) => row.description ?? emptyCellDash,
      total: emptyCell,
    }),
    column<Row>({
      header: () => "% of Contract",
      draw: ({ amountInBasisPoints }) => <Percentage percent={amountInBasisPoints} basisPoints />,
      total: emptyCell,
    }),
    numericColumn<Row>({
      header: () => "Cost",
      draw: ({ amountInBasisPoints }) =>
        priceCell({ id: "cost", valueInCents: (committedInCents * amountInBasisPoints) / 10000 }),
      total: () => priceCell({ id: "cost", valueInCents: committedInCents }),
    }),
  ];
}

function createPaymentScheduleRows(
  draws: PurchaseOrderPage_DrawFragment[],
  depositInBasisPoints: number | null | undefined,
): GridDataRow<Row>[] {
  const depositRow = depositInBasisPoints
    ? [
        {
          kind: "draw" as const,
          id: "deposit",
          data: { id: "deposit", task: undefined, amountInBasisPoints: depositInBasisPoints, isDeposit: true },
        },
      ]
    : [];
  const drawRows = draws.map((d) => ({ kind: "draw" as const, id: d.id, data: d }));
  return [simpleHeader, ...depositRow, ...drawRows, { id: "total", kind: "total" as const, data: undefined }];
}
